import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Users, X } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

interface AgencyEmployee {
  id: number;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  position: string | null;
  jobTitleId: number | null;
}

interface JobTitle {
  id: number;
  name: string;
  ownerType: string;
  agencyId: string;
}

interface Representative {
  id: number;
  userId: string;
  role: string;
  effectiveDate?: string;
  endDate?: string | null;
}

interface Team {
  id: number;
  name: string;
  description?: string;
  memberCount?: number;
}

interface AgencyRepresentativesProps {
  entityType: "client" | "prospect";
  entityId: number;
  currentTeamId?: number | null;
}

export default function AgencyRepresentatives({
  entityType,
  entityId,
  currentTeamId,
}: AgencyRepresentativesProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch agency profile to get agencyId
  const { data: profile } = useQuery({
    queryKey: ["/api/user/profile"],
    queryFn: async () => {
      const r = await fetch("/api/user/profile");
      if (!r.ok) throw new Error("Failed to load profile");
      return r.json();
    },
  });

  const agencyId = (profile?.agencyId || "").toString();

  // Fetch agency employees
  const { data: agencyEmployees = [] } = useQuery<AgencyEmployee[]>({
    queryKey: ["/api/agency/employees"],
    enabled: !!agencyId,
  });

  // Fetch job titles to filter employees
  const { data: jobTitles = [] } = useQuery<JobTitle[]>({
    queryKey: ["/api/org/job-titles", "agency", agencyId],
    queryFn: async () => {
      if (!agencyId) return [];
      const r = await fetch(`/api/org/job-titles?ownerType=agency&ownerId=${agencyId}`);
      if (!r.ok) return [];
      return r.json();
    },
    enabled: !!agencyId,
  });

  // Fetch agency teams
  const { data: teams = [] } = useQuery<Team[]>({
    queryKey: ["/api/teams"],
    queryFn: async () => {
      const r = await fetch("/api/teams");
      if (!r.ok) return [];
      return r.json();
    },
  });

  // Get job title IDs
  const producerJobTitleId = jobTitles.find(jt => jt.name === "Producer")?.id;
  const agentJobTitleId = jobTitles.find(jt => jt.name === "Agent")?.id;

  // Filter employees by job title for Producer/Agent roles
  const producerEmployees = agencyEmployees.filter(emp => 
    emp.jobTitleId === producerJobTitleId || emp.jobTitleId === agentJobTitleId
  );
  
  // All eligible employees (for backward compatibility in name lookup)
  const eligibleEmployees = agencyEmployees.filter(emp =>
    emp.jobTitleId === producerJobTitleId || 
    emp.jobTitleId === agentJobTitleId
  );

  // Fetch all representatives for this entity
  const { data: representatives = [] } = useQuery<Representative[]>({
    queryKey: [`/api/${entityType}s/${entityId}/account-managers`],
  });

  // Filter to only show ACTIVE representatives (no end_date)
  const activeRepresentatives = representatives.filter(r => !r.endDate);

  // Group representatives by role
  const primaryProducer = activeRepresentatives.find(r => r.role === "primary_producer");
  const secondaryProducer = activeRepresentatives.find(r => r.role === "secondary_producer");
  const accountManager = activeRepresentatives.find(r => r.role === "account_manager");

  // Add or update representative
  const upsertRepresentativeMutation = useMutation({
    mutationFn: async ({ userId, role }: { userId: string; role: string }) => {
      const endpoint = `${entityType === "client" ? "/api/clients" : "/api/prospects"}/${entityId}/account-managers`;
      const res = await apiRequest("POST", endpoint, { userId, role });
      return res.json();
    },
    onMutate: async (variables) => {
      const repsKey = [`/api/${entityType}s/${entityId}/account-managers`];
      await queryClient.cancelQueries({ queryKey: repsKey });
      const previous = queryClient.getQueryData<Representative[]>(repsKey);

      // End previous active assignment for this role (soft delete),
      // then add new assignment (matches backend behavior)
      const endDate = new Date().toISOString().split('T')[0];
      
      queryClient.setQueryData<Representative[]>(repsKey, (current) => {
        const list = Array.isArray(current) ? current : [];
        
        // End any active assignments for this role
        const updatedList = list.map((r) => {
          if (r.role === variables.role && !r.endDate) {
            return { ...r, endDate };
          }
          return r;
        });
        
        // Add the new assignment
        const optimistic: Representative = {
          id: -Date.now(),
          userId: variables.userId,
          role: variables.role,
          endDate: null,
        };
        return [...updatedList, optimistic];
      });

      return { previous };
    },
    onSuccess: (data: any, variables) => {
      // Just invalidate - backend handled ending previous assignment and creating new one
      queryClient.invalidateQueries({ queryKey: [`/api/${entityType}s/${entityId}/account-managers`] });
      queryClient.invalidateQueries({ queryKey: [`/api/${entityType}s/${entityId}`] });

      // Also invalidate the structured keys used across the app.
      if (entityType === "client") {
        queryClient.invalidateQueries({ queryKey: ["/api/clients"] });
        queryClient.invalidateQueries({ queryKey: ["/api/clients", entityId] });
        queryClient.invalidateQueries({ queryKey: ["/api/clients", String(entityId)] });
      }

      if (entityType === "prospect") {
        queryClient.invalidateQueries({ queryKey: ["/api/prospects"] });
        queryClient.invalidateQueries({ queryKey: ["/api/prospects", entityId] });
        queryClient.invalidateQueries({ queryKey: ["/api/prospects", String(entityId)] });
      }

      if (entityType === "client") {
        queryClient.invalidateQueries({ queryKey: ["/api/client-account-managers"] });
      }
      toast({ title: "Success", description: "Representative updated" , variant: "success"});
    },
    onError: (_err, _variables, context) => {
      const repsKey = [`/api/${entityType}s/${entityId}/account-managers`];
      if ((context as any)?.previous) {
        queryClient.setQueryData(repsKey, (context as any).previous);
      }
      toast({ title: "Error", description: "Failed to update representative", variant: "destructive" });
    },
  });

  // Remove representative
  const removeRepresentativeMutation = useMutation({
    mutationFn: async ({ userId, role }: { userId: string; role: string }) => {
      const base = `${entityType === "client" ? "/api/clients" : "/api/prospects"}/${entityId}/account-managers/${userId}`;
      const params = new URLSearchParams({ role });
      if (entityType === "client") {
        params.append("softDelete", "true");
      }
      const endpoint = `${base}?${params.toString()}`;
      await apiRequest("DELETE", endpoint);
    },
    onMutate: async (variables) => {
      const repsKey = [`/api/${entityType}s/${entityId}/account-managers`];
      await queryClient.cancelQueries({ queryKey: repsKey });
      const previous = queryClient.getQueryData<Representative[]>(repsKey);

      // Set end_date instead of removing (we use soft deletes now)
      const endDate = new Date().toISOString().split('T')[0];
      
      queryClient.setQueryData<Representative[]>(repsKey, (current) => {
        const list = Array.isArray(current) ? current : [];
        return list.map((r) => {
          // Match the record to end and only end if it's currently active
          if (r.userId === variables.userId && r.role === variables.role && !r.endDate) {
            return { ...r, endDate };
          }
          return r;
        });
      });

      return { previous };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/${entityType}s/${entityId}/account-managers`] });
      queryClient.invalidateQueries({ queryKey: [`/api/${entityType}s/${entityId}`] });

      if (entityType === "client") {
        queryClient.invalidateQueries({ queryKey: ["/api/clients"] });
        queryClient.invalidateQueries({ queryKey: ["/api/clients", entityId] });
        queryClient.invalidateQueries({ queryKey: ["/api/clients", String(entityId)] });
      }

      if (entityType === "prospect") {
        queryClient.invalidateQueries({ queryKey: ["/api/prospects"] });
        queryClient.invalidateQueries({ queryKey: ["/api/prospects", entityId] });
        queryClient.invalidateQueries({ queryKey: ["/api/prospects", String(entityId)] });
      }

      if (entityType === "client") {
        queryClient.invalidateQueries({ queryKey: ["/api/client-account-managers"] });
      }
      toast({ title: "Success", description: "Representative removed" , variant: "success"});
    },
    onError: (_err, _variables, context) => {
      const repsKey = [`/api/${entityType}s/${entityId}/account-managers`];
      if ((context as any)?.previous) {
        queryClient.setQueryData(repsKey, (context as any).previous);
      }
      toast({ title: "Error", description: "Failed to remove representative", variant: "destructive" });
    },
  });

  // Update team assignment
  const updateTeamMutation = useMutation({
    mutationFn: async (teamId: number | null) => {
      const endpoint = entityType === "client" ? `/api/clients/${entityId}` : `/api/prospects/${entityId}`;
      const res = await apiRequest("PATCH", endpoint, { teamId });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/${entityType}s/${entityId}`] });
      queryClient.invalidateQueries({ queryKey: [`/api/${entityType}s`] });
      toast({ title: "Success", description: "Team updated" , variant: "success"});
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to update team", variant: "destructive" });
    },
  });

  const handleRoleChange = (role: string, userId: string) => {
    upsertRepresentativeMutation.mutate({ userId, role });
  };

  const handleRemove = (userId: string, role: string) => {
    removeRepresentativeMutation.mutate({ userId, role });
  };

  const handleTeamChange = (teamId: string) => {
    if (teamId === "none") {
      updateTeamMutation.mutate(null);
    } else {
      updateTeamMutation.mutate(parseInt(teamId, 10));
    }
  };

  const handleRemoveTeam = () => {
    updateTeamMutation.mutate(null);
  };

  const getEmployeeName = (userId: string | undefined) => {
    if (!userId) return "";
    // First try eligible employees (producers/agents), then fall back to all agency employees
    // This ensures names display correctly even if the employee's status or job title changed
    let employee = eligibleEmployees.find(e => String(e.id) === userId);
    if (!employee) {
      employee = agencyEmployees.find(e => String(e.id) === userId);
    }
    if (!employee) return "";
    return `${employee.firstName || ''} ${employee.lastName || ''}`.trim() || employee.email || "";
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="h-5 w-5" />
          Agency Representatives
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">Producer/Agent:</label>
            <div className="flex gap-2">
              <Select
                value={primaryProducer?.userId}
                onValueChange={(value) => handleRoleChange("primary_producer", value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select Producer" />
                </SelectTrigger>
                <SelectContent>
                  {producerEmployees.map((employee) => (
                    <SelectItem key={employee.id} value={String(employee.id)}>
                      {getEmployeeName(String(employee.id))}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {primaryProducer && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleRemove(primaryProducer.userId, "primary_producer")}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Secondary Producer:</label>
            <div className="flex gap-2">
              <Select
                value={secondaryProducer?.userId}
                onValueChange={(value) => handleRoleChange("secondary_producer", value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select Secondary Producer" />
                </SelectTrigger>
                <SelectContent>
                  {producerEmployees.map((employee) => (
                    <SelectItem key={employee.id} value={String(employee.id)}>
                      {getEmployeeName(String(employee.id))}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {secondaryProducer && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleRemove(secondaryProducer.userId, "secondary_producer")}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Account Manager:</label>
            <div className="flex gap-2">
              <Select
                value={accountManager?.userId}
                onValueChange={(value) => handleRoleChange("account_manager", value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select Account Manager" />
                </SelectTrigger>
                <SelectContent>
                  {agencyEmployees.map((employee) => (
                    <SelectItem key={employee.id} value={String(employee.id)}>
                      {getEmployeeName(String(employee.id)) || `${employee.firstName || ''} ${employee.lastName || ''}`.trim() || employee.email || `Employee ${employee.id}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {accountManager && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleRemove(accountManager.userId, "account_manager")}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Team:</label>
            <div className="flex gap-2">
              <Select
                value={currentTeamId ? String(currentTeamId) : "none"}
                onValueChange={handleTeamChange}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select Team" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Team</SelectItem>
                  {teams.map((team) => (
                    <SelectItem key={team.id} value={String(team.id)}>
                      {team.name} {team.memberCount ? `(${team.memberCount} members)` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {currentTeamId && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleRemoveTeam}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
